import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  WhatsApp,
  Email,
  Schedule,
  Save,
} from '@mui/icons-material';
import api from '../../services/api';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
  { value: 'UTC', label: 'UTC' },
];

const SupportSettingsContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    whatsapp: {
      enabled: true,
      phoneNumber: '+1 (503) 719-9075',
      defaultMessage: 'Hi! I need help with Novo Solution.',
    },
    businessHours: {
      timezone: 'America/Los_Angeles',
      displayTimezone: 'PST',
      schedule: DAYS_OF_WEEK.map(day => ({
        day: day.key,
        isOpen: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day.key),
        openTime: '09:00',
        closeTime: '17:00',
      })),
      afterHoursMessage: 'Thank you for contacting Novo Solution support. Our business hours are Monday - Friday, 9:00 AM - 5:00 PM PST. We will respond to your message during the next business day.',
    },
    email: {
      enabled: true,
      address: 'support@novosolution.org',
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/config/support-settings');
      if (response.data.success && response.data.supportSettings) {
        setSettings(response.data.supportSettings);
      }
    } catch (err) {
      console.error('Error fetching support settings:', err);
      setError('Failed to load support settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/config/support-settings', {
        supportSettings: settings,
        updatedBy: 'admin',
      });
      
      if (response.data.success) {
        setSuccess('Support settings saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error saving support settings:', err);
      setError('Failed to save support settings');
    } finally {
      setSaving(false);
    }
  };

  const updateWhatsApp = (field, value) => {
    setSettings(prev => ({
      ...prev,
      whatsapp: { ...prev.whatsapp, [field]: value },
    }));
  };

  const updateBusinessHours = (field, value) => {
    setSettings(prev => ({
      ...prev,
      businessHours: { ...prev.businessHours, [field]: value },
    }));
  };

  const updateScheduleDay = (dayKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        schedule: prev.businessHours.schedule.map(day =>
          day.day === dayKey ? { ...day, [field]: value } : day
        ),
      },
    }));
  };

  const updateEmail = (field, value) => {
    setSettings(prev => ({
      ...prev,
      email: { ...prev.email, [field]: value },
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* WhatsApp Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WhatsApp sx={{ mr: 1, color: '#25D366' }} />
                <Typography variant="h6">WhatsApp Chat Support</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.whatsapp?.enabled ?? true}
                    onChange={(e) => updateWhatsApp('enabled', e.target.checked)}
                  />
                }
                label="Enable WhatsApp Support"
              />
              <TextField
                fullWidth
                label="WhatsApp Business Number"
                value={settings.whatsapp?.phoneNumber || ''}
                onChange={(e) => updateWhatsApp('phoneNumber', e.target.value)}
                sx={{ mt: 2 }}
                helperText="Format: +1 (XXX) XXX-XXXX"
              />
              <TextField
                fullWidth
                label="Default Message"
                value={settings.whatsapp?.defaultMessage || ''}
                onChange={(e) => updateWhatsApp('defaultMessage', e.target.value)}
                sx={{ mt: 2 }}
                multiline
                rows={2}
                helperText="Pre-filled message when user opens WhatsApp"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Email Support Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Email sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6">Email Support</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.email?.enabled ?? true}
                    onChange={(e) => updateEmail('enabled', e.target.checked)}
                  />
                }
                label="Enable Email Support"
              />
              <TextField
                fullWidth
                label="Support Email Address"
                value={settings.email?.address || ''}
                onChange={(e) => updateEmail('address', e.target.value)}
                sx={{ mt: 2 }}
                type="email"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Business Hours */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: '#ff9800' }} />
                <Typography variant="h6">Business Hours</Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={settings.businessHours?.timezone || 'America/Los_Angeles'}
                      label="Timezone"
                      onChange={(e) => updateBusinessHours('timezone', e.target.value)}
                    >
                      {TIMEZONES.map(tz => (
                        <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Timezone Label"
                    value={settings.businessHours?.displayTimezone || 'PST'}
                    onChange={(e) => updateBusinessHours('displayTimezone', e.target.value)}
                    helperText="Shown to users (e.g., PST, EST)"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Weekly Schedule</Typography>

              <Grid container spacing={1}>
                {DAYS_OF_WEEK.map(day => {
                  const daySchedule = settings.businessHours?.schedule?.find(s => s.day === day.key) || {
                    isOpen: false, openTime: '09:00', closeTime: '17:00'
                  };
                  return (
                    <Grid item xs={12} key={day.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={daySchedule.isOpen}
                              onChange={(e) => updateScheduleDay(day.key, 'isOpen', e.target.checked)}
                              size="small"
                            />
                          }
                          label={day.label}
                          sx={{ width: 140 }}
                        />
                        {daySchedule.isOpen && (
                          <>
                            <TextField
                              type="time"
                              value={daySchedule.openTime}
                              onChange={(e) => updateScheduleDay(day.key, 'openTime', e.target.value)}
                              size="small"
                              sx={{ width: 130 }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <Typography>to</Typography>
                            <TextField
                              type="time"
                              value={daySchedule.closeTime}
                              onChange={(e) => updateScheduleDay(day.key, 'closeTime', e.target.value)}
                              size="small"
                              sx={{ width: 130 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </>
                        )}
                        {!daySchedule.isOpen && (
                          <Typography color="text.secondary">Closed</Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <TextField
                fullWidth
                label="After-Hours Auto-Response Message"
                value={settings.businessHours?.afterHoursMessage || ''}
                onChange={(e) => updateBusinessHours('afterHoursMessage', e.target.value)}
                multiline
                rows={3}
                helperText="Message shown when users contact support outside business hours"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              size="large"
            >
              {saving ? 'Saving...' : 'Save Support Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupportSettingsContent;

